/**
 * One node of a when-clause, drawn as a row or a group. Ported from t3code.
 *
 * Recursive, and the recursion is the point: `a && (b || !c)` is a group
 * holding a condition and a nested group. The three shapes it renders are the
 * three a node can be — a single condition, a negated group, and an and/or
 * group — and `conditionParts` is what decides which, so `!a` draws as one row
 * with its Not toggle on rather than as a box around a box.
 *
 * Variant names are Forge's, not t3code's: `size="xs"` here is `compact` there.
 *
 * @module
 */
import type { KeybindingWhenNode } from "@cmmd-forge/contracts";
import { MinusIcon, PlusIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Toggle } from "../ui/toggle";
import {
  type BooleanOperator,
  buildWhenExpressionGroup,
  conditionParts,
  defaultWhenCondition,
  flattenWhenChildren,
  setConditionIdentifier,
  setConditionNegated,
  whenAstToExpression,
} from "./keybindingWhenDraft";

export interface WhenExpressionNodeEditorProps {
  readonly node: KeybindingWhenNode;
  readonly variables: ReadonlyArray<string>;
  /** Nesting depth, used only to soften the chrome of an inner group. */
  readonly depth?: number;
  readonly onChange: (node: KeybindingWhenNode) => void;
  /** Absent on the root, which cannot be removed without emptying the clause. */
  readonly onRemove?: (() => void) | undefined;
}

/** The variable picker, which also offers an identifier it does not recognise. */
function WhenVariableSelect({
  value,
  variables,
  isUnknown,
  onChange,
}: {
  readonly value: string;
  readonly variables: ReadonlyArray<string>;
  readonly isUnknown: boolean;
  readonly onChange: (value: string) => void;
}) {
  // An unknown identifier is prepended rather than dropped: `keybindings.json`
  // is hand-editable, so a clause can name a context that was renamed. Omitting
  // it would leave the select blank and silently rewrite the rule on save.
  const options = variables.includes(value) ? variables : [value, ...variables];

  return (
    <Select value={value} onValueChange={(next) => next && onChange(next)}>
      <SelectTrigger
        size="xs"
        aria-invalid={isUnknown}
        className="min-w-0 flex-1 font-mono"
        aria-label="Condition variable"
      >
        <SelectValue placeholder="Condition" />
      </SelectTrigger>
      <SelectContent alignItemWithTrigger={false} className="max-h-72 w-fit min-w-44">
        {options.map((option) => (
          <SelectItem key={option} value={option} className="min-h-7 w-full py-1 font-mono text-xs">
            <span className="truncate">{option}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** The vertical rule and elbow that tie a child to its parent group. */
function ChildConnector({ depth }: { readonly depth: number }) {
  const tone = depth === 0 ? "bg-border" : "bg-border/70";
  return (
    <>
      <span className={cn("absolute top-0 bottom-0 left-1.5 w-px", tone)} aria-hidden />
      <span className={cn("absolute top-4 left-1.5 h-px w-2.5", tone)} aria-hidden />
    </>
  );
}

export function WhenExpressionNodeEditor({
  node,
  variables,
  depth = 0,
  onChange,
  onRemove,
}: WhenExpressionNodeEditorProps) {
  const condition = conditionParts(node);

  if (condition) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border/70 bg-background/60 px-2 py-2">
        <Toggle
          pressed={condition.negated}
          onPressedChange={(pressed) => onChange(setConditionNegated(node, pressed))}
          aria-label={`Negate ${condition.identifier}`}
          variant="outline"
          size="xs"
          className="min-w-10"
        >
          Not
        </Toggle>
        <WhenVariableSelect
          value={condition.identifier}
          variables={variables}
          isUnknown={!variables.includes(condition.identifier)}
          onChange={(value) => onChange(setConditionIdentifier(node, value))}
        />
        {onRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="size-7"
            aria-label={`Remove condition ${condition.identifier}`}
            onClick={onRemove}
          >
            <MinusIcon className="size-3.5" />
          </Button>
        ) : null}
      </div>
    );
  }

  if (node.type === "not") {
    return (
      <div
        className={cn(
          "space-y-2 rounded-lg border border-border/70 bg-muted/20 p-2",
          depth > 0 && "border-border/50 bg-background/50",
        )}
      >
        <div className="flex items-center gap-2">
          <Toggle
            pressed
            // Un-pressing unwraps rather than deleting: the group's contents are
            // what the reader built, and losing them to a toggle would be a
            // destructive action wearing a switch.
            onPressedChange={(pressed) => onChange(pressed ? node : node.node)}
            aria-label="Negate group"
            variant="outline"
            size="xs"
            className="min-w-10"
          >
            Not
          </Toggle>
          {onRemove ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="ml-auto size-7"
              aria-label="Remove negated group"
              onClick={onRemove}
            >
              <MinusIcon className="size-3.5" />
            </Button>
          ) : null}
        </div>
        <div className="relative pl-4">
          <ChildConnector depth={depth} />
          <WhenExpressionNodeEditor
            node={node.node}
            variables={variables}
            depth={depth + 1}
            onChange={(next) => onChange({ type: "not", node: next })}
          />
        </div>
      </div>
    );
  }

  const operator: BooleanOperator = node.type === "or" ? "or" : "and";
  const children = flattenWhenChildren(node, operator);

  // Two identical siblings are legal (`a && a`), so identity alone is not a
  // stable key. The occurrence index disambiguates them without keying on
  // array position, which would reorder the DOM on every removal.
  const seen = new Map<string, number>();
  const childEntries = children.map((child) => {
    const base = `${child.type}-${whenAstToExpression(child)}`;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return { child, key: count === 0 ? base : `${base}-${count}` };
  });

  const replaceChildren = (next: ReadonlyArray<KeybindingWhenNode>, op = operator) => {
    const rebuilt = buildWhenExpressionGroup(next, op);
    // An emptied group becomes a single condition rather than vanishing: the
    // clause still exists, and the reader still has something to edit.
    onChange(rebuilt ?? defaultWhenCondition());
  };

  return (
    <div
      className={cn(
        "space-y-2 rounded-lg border border-border/60 bg-muted/10 p-2",
        depth > 0 && "border-border/70 bg-background/55",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={operator}
          onValueChange={(value) => {
            if (value && value !== operator) replaceChildren(children, value as BooleanOperator);
          }}
        >
          <SelectTrigger size="xs" className="w-24" aria-label="Group operator">
            <SelectValue />
          </SelectTrigger>
          <SelectContent alignItemWithTrigger={false} className="w-fit min-w-24">
            <SelectItem value="and" className="min-h-7 py-1 font-mono text-xs">
              and
            </SelectItem>
            <SelectItem value="or" className="min-h-7 py-1 font-mono text-xs">
              or
            </SelectItem>
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={() => replaceChildren([...children, defaultWhenCondition()])}
        >
          <PlusIcon className="size-3.5" />
          Condition
        </Button>
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={() => {
            // The nested group takes the opposite operator. Nesting `and` in
            // `and` is a group that means nothing — it flattens straight back
            // out — so the only useful new group is the other one.
            const nested: BooleanOperator = operator === "and" ? "or" : "and";
            replaceChildren([
              ...children,
              {
                type: nested,
                left: defaultWhenCondition(),
                right: { type: "not", node: defaultWhenCondition() },
              },
            ]);
          }}
        >
          <PlusIcon className="size-3.5" />
          Group
        </Button>
        {onRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="ml-auto size-7"
            aria-label="Remove group"
            onClick={onRemove}
          >
            <MinusIcon className="size-3.5" />
          </Button>
        ) : null}
      </div>
      <div className="space-y-2">
        {childEntries.map(({ child, key }, index) => (
          <div key={key} className="relative pl-4">
            <ChildConnector depth={depth} />
            <WhenExpressionNodeEditor
              node={child}
              variables={variables}
              depth={depth + 1}
              onChange={(next) =>
                replaceChildren(children.map((existing, at) => (at === index ? next : existing)))
              }
              onRemove={() => replaceChildren(children.filter((_, at) => at !== index))}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
